import React, { useState, useCallback, useMemo, useEffect } from "react";
import Papa from "papaparse";
import { DEFAULT_COMMISSION_PERCENTAGE } from "../utils/constants";
import {
  processAffiliateData,
  calculateSummaryStats,
  sortData,
  parseNumber,
} from "../utils/helpers";
import useCSVProcessor from "../hooks/useCSVProcessor";
import useSupabaseData from "../hooks/useSupabaseData";

// Components
import Header from "./Header";
import Alert from "./ui/Alert";
import FileUploader from "./FileUploader";
import SummaryCards from "./SummaryCards";
import AffiliateTable from "./AffiliateTable";
import AffiliateDetailModal from "./AffiliateDetailModal";
import DateRangePicker from "./DateRangePicker";
import ShareDashboardButton from "./ShareDashboardButton";

const OldDashboard = () => {
  // Supabase data management
  const {
    isLoading: supabaseLoading,
    error: supabaseError,
    success: supabaseSuccess,
    debugInfo,
    loadData,
    importCSVData,
    getAvailableDates,
    getDateRangeSummary,
    clearMessages: clearSupabaseMessages,
  } = useSupabaseData();

  // CSV Data and processing state
  const [csvData, setCsvData] = useState(null);
  const [supabaseData, setSupabaseData] = useState(null);
  const {
    isLoading: csvLoading,
    error: csvError,
    success: csvSuccess,
    debugInfo: csvDebugInfo,
    processFile,
    clearMessages,
  } = useCSVProcessor();

  // Settings state
  const [commissionPercentage, setCommissionPercentage] = useState(
    DEFAULT_COMMISSION_PERCENTAGE
  );
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Date filtering state
  const [dateFilterType, setDateFilterType] = useState("all"); // 'all', 'single', 'range'
  const [singleDate, setSingleDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");

  // Table sorting and modal state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);

  // State for date information
  const [availableDates, setAvailableDates] = useState([]);
  const [dateRangeSummary, setDateRangeSummary] = useState(null);

  // Load data from Supabase on app start and apply URL parameters
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const data = await loadData();
        if (data && data.length > 0) {
          setSupabaseData(data);
          console.log("Loaded", data.length, "records from Supabase");
        }

        // Load available dates and summary
        const dates = await getAvailableDates();
        const summary = await getDateRangeSummary();
        setAvailableDates(dates);
        setDateRangeSummary(summary);

        // Apply URL parameters if present
        const urlParams = new URLSearchParams(window.location.search);

        // Apply date filter
        const dateFilter = urlParams.get("dateFilter");
        if (dateFilter && ["single", "range"].includes(dateFilter)) {
          setDateFilterType(dateFilter);

          if (dateFilter === "single") {
            const date = urlParams.get("date");
            if (date) setSingleDate(date);
          } else if (dateFilter === "range") {
            const startDate = urlParams.get("startDate");
            const endDate = urlParams.get("endDate");
            if (startDate) setDateRangeStart(startDate);
            if (endDate) setDateRangeEnd(endDate);
          }
        }

        // Apply commission percentage
        const commission = urlParams.get("commission");
        if (commission) {
          const commissionValue = parseFloat(commission);
          if (!isNaN(commissionValue)) {
            setCommissionPercentage(commissionValue);
          }
        }

        // Apply sort configuration
        const sortBy = urlParams.get("sortBy");
        const sortDir = urlParams.get("sortDir");
        if (sortBy && ["asc", "desc"].includes(sortDir)) {
          setSortConfig({ key: sortBy, direction: sortDir });
        }
      } catch (err) {
        console.log(
          "No existing data in Supabase or error loading:",
          err.message
        );
      }
    };

    loadInitialData();
  }, [loadData, getAvailableDates, getDateRangeSummary]);

  // Convert Supabase data to CSV format for processing
  const csvDataFromSupabase = useMemo(() => {
    if (!supabaseData) return null;

    return supabaseData.map((row) => ({
      Date: row.date || "",
      "Ad Name": row.ad_name || "",
      "Campaign Name": row.campaign_name || "",
      "Adset Name": row.adset_name || "",
      "Ad Spend": row.spend || 0,
      "Pixel CV": row.revenue || 0,
      ROAS: row.spend > 0 ? row.revenue / row.spend : 0, // Calculate ROAS
      "Pixel ROAS": row.spend > 0 ? row.revenue / row.spend : 0, // Same as ROAS
      "Pixel Purchases": row.purchases || 0,
      "Pixel CPA": row.cpa || "",
      "Pixel AOV": row.aov || "",
      Profit: row.profit || 0,
      Commission: row.commission || 0,
      Clicks: row.clicks || 0,
      Impressions: row.impressions || 0,
      // Add affiliate extraction logic
      affiliate: row.affiliate_name || "",
    }));
  }, [supabaseData]);

  // Use either uploaded CSV or Supabase data
  const activeData = csvData || csvDataFromSupabase;

  // Filter data by date
  const filteredCsvData = useMemo(() => {
    if (!activeData || dateFilterType === "all") return activeData;

    return activeData.filter((row) => {
      const rowDate = row["Date"];
      if (!rowDate) return true;

      // Parse the date (handle various formats)
      const date = new Date(rowDate);
      if (isNaN(date.getTime())) return true;

      const dateStr = date.toISOString().split("T")[0];

      if (dateFilterType === "single") {
        return dateStr === singleDate;
      } else if (dateFilterType === "range") {
        return dateStr >= dateRangeStart && dateStr <= dateRangeEnd;
      }

      return true;
    });
  }, [activeData, dateFilterType, singleDate, dateRangeStart, dateRangeEnd]);

  // Process CSV data into affiliate statistics using tiered commission system
  const processedData = useMemo(() => {
    if (!filteredCsvData) return null;

    const result = processAffiliateData(filteredCsvData);
    console.log(`📊 Data Processing Summary:`);
    console.log(`   • Total rows in dataset: ${filteredCsvData.length}`);
    console.log(`   • Processed rows (with spend): ${result.processedRows}`);
    console.log(`   • Zero-spend rows: ${result.skippedRows}`);
    console.log(`   • Final affiliates: ${result.affiliates.length}`);

    // Calculate raw totals from source data for comparison
    const rawTotalSpend = filteredCsvData.reduce(
      (sum, row) => sum + parseNumber(row["Ad Spend"]),
      0
    );
    const rawTotalRevenue = filteredCsvData.reduce(
      (sum, row) => sum + parseNumber(row["Pixel CV"]),
      0
    );
    console.log(`💰 Raw Data Totals:`);
    console.log(
      `   • Total spend (all records): $${rawTotalSpend.toLocaleString()}`
    );
    console.log(
      `   • Total revenue (all records): $${rawTotalRevenue.toLocaleString()}`
    );

    return result.affiliates;
  }, [filteredCsvData]);

  // Sort the processed data
  const sortedData = useMemo(() => {
    if (!processedData) return null;

    if (sortConfig.key) {
      return sortData(processedData, sortConfig);
    } else {
      // Default sort by commission desc
      return sortData(processedData, {
        key: "totalCommission",
        direction: "desc",
      });
    }
  }, [processedData, sortConfig]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    return calculateSummaryStats(processedData);
  }, [processedData]);

  // Handle file upload - now saves to Supabase (APPEND MODE)
  const handleFileUpload = useCallback(
    async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        // First process the CSV to validate it
        await new Promise((resolve, reject) => {
          processFile(file, async (data) => {
            try {
              // Save to Supabase using the optimized bulk insert (APPENDS to existing data)
              await importCSVData(data);

              // Load fresh data from Supabase
              const freshData = await loadData();
              setSupabaseData(freshData);
              setCsvData(null); // Clear temporary CSV data

              // Refresh date information after upload
              const dates = await getAvailableDates();
              const summary = await getDateRangeSummary();
              setAvailableDates(dates);
              setDateRangeSummary(summary);

              resolve();
            } catch (err) {
              reject(err);
            }
          });
        });
      } catch (err) {
        console.error("Error uploading to Supabase:", err);
      }
    },
    [
      processFile,
      importCSVData,
      loadData,
      getAvailableDates,
      getDateRangeSummary,
    ]
  );

  // Handle sort
  const handleSort = useCallback((key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "desc"
          ? "asc"
          : "desc",
    }));
  }, []);

  // Export results to CSV
  const exportResults = useCallback(() => {
    if (!processedData) return;

    const exportData = processedData.map((affiliate) => ({
      "Affiliate Name": affiliate.name,
      "Ad Count": affiliate.adCount,
      "Total Ad Spend": `$${affiliate.totalAdSpend.toFixed(2)}`,
      ROAS: affiliate.avgROAS ? affiliate.avgROAS.toFixed(2) : "0.00",
      "Avg Commission Rate": affiliate.avgCommissionRate
        ? `${(affiliate.avgCommissionRate * 100).toFixed(2)}%`
        : "0.00%",
      "Total Purchases": affiliate.totalPurchases || 0,
      Commission: `$${affiliate.totalCommission.toFixed(2)}`,
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `affiliate-performance-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [processedData]);

  // Combined loading state
  const isLoading = csvLoading || supabaseLoading;

  // Combined error handling
  const error = csvError || supabaseError;
  const success = csvSuccess || supabaseSuccess;
  const combinedDebugInfo = csvDebugInfo || debugInfo;

  const clearAllMessages = useCallback(() => {
    clearMessages();
    clearSupabaseMessages();
  }, [clearMessages, clearSupabaseMessages]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header Actions */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Date Picker - Top positioned when data exists */}
            {activeData && (
              <div className="mb-6 flex justify-between items-center">
                <DateRangePicker
                  dateFilterType={dateFilterType}
                  setDateFilterType={setDateFilterType}
                  singleDate={singleDate}
                  setSingleDate={setSingleDate}
                  dateRangeStart={dateRangeStart}
                  setDateRangeStart={setDateRangeStart}
                  dateRangeEnd={dateRangeEnd}
                  setDateRangeEnd={setDateRangeEnd}
                  availableDates={availableDates}
                  dateRangeSummary={dateRangeSummary}
                />
              </div>
            )}
          </div>

          {/* Alerts */}
          {error && (
            <Alert
              type="error"
              message={error}
              onClose={clearAllMessages}
              details={combinedDebugInfo}
              showDetails={showDebugInfo}
              onToggleDetails={() => setShowDebugInfo(!showDebugInfo)}
            />
          )}

          {success && (
            <Alert
              type="success"
              message={success}
              onClose={clearAllMessages}
            />
          )}

          {/* File Upload or Results */}
          {!activeData ? (
            <FileUploader
              onFileUpload={handleFileUpload}
              isLoading={isLoading}
            />
          ) : (
            <>
              {/* Summary Cards */}
              <SummaryCards summaryStats={summaryStats} />

              {/* Affiliate Performance Table */}
              <AffiliateTable
                sortedData={sortedData}
                onSort={handleSort}
                sortConfig={sortConfig}
                onViewDetails={setSelectedAffiliate}
                onExport={exportResults}
                processedData={processedData}
              />
            </>
          )}
        </div>
      </main>

      {/* Floating Share Button (when data exists) */}
      {activeData && (
        <ShareDashboardButton
          dateFilterType={dateFilterType}
          singleDate={singleDate}
          dateRangeStart={dateRangeStart}
          dateRangeEnd={dateRangeEnd}
          commissionPercentage={commissionPercentage}
          sortConfig={sortConfig}
        />
      )}

      {/* Affiliate Detail Modal */}
      {selectedAffiliate && (
        <AffiliateDetailModal
          affiliate={selectedAffiliate}
          onClose={() => setSelectedAffiliate(null)}
        />
      )}
    </div>
  );
};

export default OldDashboard;
